import { DailyParticipant, DailyCall } from '@daily-co/daily-js';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { VideoGrid } from '@/components/video/VideoGrid';
import { EngagementHeatmap } from '@/components/charts/EngagementHeatmap';
import { EngagementTrendline } from '@/components/charts/EngagementTrendline';
import { EngagementDistributionChart } from '@/components/charts/EngagementDistributionChart';
import { useSessionStore } from '@/store/sessionStore';
import { useEngagementEmitter } from '@/hooks/useEngagementEmitter';
import { apiClient } from '@/lib/api';
import type { AggregatedEngagement, EngagementSnapshot } from '@/types/emotion';
import { initDailyCall, destroyDailyCall } from '@/lib/daily';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';
import { SessionControls } from '@/components/video/SessionControls';
import { Toaster, toast } from 'sonner';


// TOAST 
// Calculates average engagement of incoming packet and triggers alert.
let lastAlertTime = 0;
const ALERT_COOLDOWN = 120 * 1000; // 2 Minutes (Adjustable)

const checkCriticalEngagement = (snapshots: EngagementSnapshot[]) => {
  if (!snapshots.length) return;

  // 1. Calculate Average of incoming batch
  const totalEngaged = snapshots.reduce((sum, s) => sum + s.engaged, 0);
  const average = totalEngaged / snapshots.length;

  // 2. Threshold Check (< 0.3)
  if (average < 0.3) {
    const now = Date.now();
    
    // 3. Cooldown Check
    if (now - lastAlertTime > ALERT_COOLDOWN) {
      toast.error(`⚠️ Class Engagement Critical: ${Math.round(average * 100)}%`, {
        description: "Multiple students are disengaged. Consider a break.",
        duration: 8000, // Stays up longer for visibility
        position: 'top-center',
        style: { background: '#FF2A68', color: '#F4F4F5', border: 'none' } // Neon Red alert
      });
      lastAlertTime = now;
    }
  }
};

export const InstructorDashboard = () => {
  const { sessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [participants, setParticipants] = useState<DailyParticipant[]>([]);
  const [roomUrl, setRoomUrl] = useState<string | null>(location.state?.roomUrl ?? null);
  const [isJoiningCall, setIsJoiningCall] = useState(true);
  const [callError, setCallError] = useState<string | null>(null);
  
  // Guard Refs
  const callRef = useRef<DailyCall | null>(null);
  
  // ✅ FIX: Restored the definition so the bottom useEffect works
  const isMountedRef = useRef(true);

  const { snapshots, addSnapshot, setSession } = useSessionStore();

  const { publish } = useEngagementEmitter({
    sessionId: sessionId ?? '',
    studentId: 'instructor',
    role: 'instructor',
    enabled: Boolean(sessionId),
    onServerUpdate: ({ snapshots: incoming }) => {
      incoming.forEach((snapshot) => addSnapshot(snapshot));
      
      //Toast function
      checkCriticalEngagement(incoming);
    }
  });

  const { data: analytics, isLoading: isLoadingAnalytics, error: analyticsError, refetch: fetchAnalytics } = useQuery({
    enabled: Boolean(sessionId),
    queryKey: ['analytics', sessionId],
    queryFn: async () => {
      try {
        const token = await getToken();
        const response = await apiClient.get<{ engagement: AggregatedEngagement[]; meta: { roomUrl: string } }>(
          `/sessions/${sessionId}/analytics`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update URL if we found one
        setRoomUrl((prev) => prev ?? response.data.meta.roomUrl);
        return response.data;
      } catch (e) {
        console.error("Analytics fetch failed:", e);
        throw e;
      }
    }
  });

  const [refreshRate] = useState(2000);

  // 1. VIDEO CALL EFFECT
  useEffect(() => {
    if (!sessionId || !roomUrl) return;
    setSession({ sessionId, roomUrl, role: 'instructor' });

    if (callRef.current) return;

    const joinCall = async () => {
      try {
        const call = await initDailyCall(roomUrl);
        callRef.current = call;
        
        await call.join();
        setIsJoiningCall(false);
        
        const updateParticipants = () => setParticipants(Object.values(call.participants() ?? {}));
        call.on('participant-joined', updateParticipants);
        call.on('participant-updated', updateParticipants);
        call.on('participant-left', updateParticipants);
        updateParticipants();
      } catch (err) {
        console.error("Daily Join Error:", err);
        if (!(err as Error).message?.includes('Duplicate')) {
            setCallError('Unable to join Daily room.');
        }
        setIsJoiningCall(false);
      }
    };
    joinCall();

    return () => {
      console.log("🧹 Cleanup: Leaving call");
      // FIX 2: Do NOT set a global "isMounted = false" here.
      // Just clean up the video.
      const callObject = callRef.current;
      if (callObject) {
        callObject.leave().catch(() => {});
        callObject.destroy().catch(() => {});
      }
      destroyDailyCall();
      callRef.current = null;
    };
  }, [roomUrl, sessionId, setSession, getToken]);


  // 2. DATA INTERVAL EFFECT
  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(() => {
      fetchAnalytics();
    }, refreshRate);
    
    return () => clearInterval(interval);
  }, [refreshRate, fetchAnalytics]);


  // 3. SPIDER CHART LOGIC (Live + DB Hybrid)
  const sessionSummary = useMemo(() => {
    // A. Try DB Data first
    let sourceData = analytics?.engagement;

    // B. If DB is empty, use Live Socket Data
    if (!sourceData || sourceData.length === 0) {
        sourceData = (snapshots as EngagementSnapshot[])
            .filter(s => s.sessionId === sessionId) as any;
    }

    if (!sourceData?.length) return null;

    const total = sourceData.length;
    const totals = sourceData.reduce(
      (acc, curr) => {
        acc.engaged += curr.engaged;
        acc.confused += curr.confused;
        acc.disengaged += curr.disengaged;
        acc.neutral += curr.neutral;
        return acc;
      },
      { engaged: 0, confused: 0, disengaged: 0, neutral: 0 }
    );
    return {
      engaged: Math.round((totals.engaged / total) * 100),
      confused: Math.round((totals.confused / total) * 100),
      disengaged: Math.round((totals.disengaged / total) * 100),
      neutral: Math.round((totals.neutral / total) * 100)
    };
  }, [analytics, snapshots, sessionId]);

  const handleManualSnapshot = () => {
    const manualData = {
      angry: 0, disgust: 0, fear: 0, happy: 0.5, sad: 0.2, surprise: 0.2, neutral: 0.1
    };
    const engaged = manualData.happy + manualData.surprise;
    const confused = manualData.fear + manualData.disgust;
    const disengaged = manualData.sad + manualData.angry;

    publish({
      ...manualData, engaged, confused, disengaged, timestamp: new Date().toISOString()
    });
  };

  if (!sessionId) return <ErrorState message="Invalid session ID" />;
  
  const currentSessionSnapshots = (snapshots as EngagementSnapshot[]).filter(
    (s) => s.sessionId === sessionId
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="relative space-y-4 rounded-2xl border border-background-border bg-background-surface p-4 lg:col-span-3">
          <p className="text-sm text-text-dimmed">Daily.co call</p>
          {callError && <ErrorState message={callError} />}
          {isJoiningCall && <LoadingOverlay label="Joining Daily room..." />}
          
          {!callError && <VideoGrid participants={participants} />}
          
          <div className="border-t border-background-border pt-4">
             <SessionControls isHost={true} />
          </div>
        </div>
        
        <div className="rounded-2xl border border-background-border bg-background-surface p-4 lg:col-span-2">
          <p className="text-sm text-text-dimmed">Session controls</p>
          <div className="my-4 rounded-xl bg-neon-gradient/20 border border-brand/30 p-4 text-center shadow-glow">
            <p className="text-xs font-bold uppercase tracking-widest text-brand mb-1">
              Student Join Code
            </p>
            <button
              onClick={() => navigator.clipboard.writeText(sessionId || '')}
              className="text-4xl font-black text-text-primary tracking-widest hover:text-brand hover:scale-105 transition-all cursor-pointer font-mono"
            >
              {sessionId}
            </button>
          </div>
          <button
            type="button"
            onClick={handleManualSnapshot}
            className="mt-4 w-full rounded-lg border border-background-border px-4 py-2 text-sm text-text-primary hover:border-brand hover:shadow-glow transition-all"
          >
            Insert manual checkpoint
          </button>
        </div>
      </div>

      {analyticsError && <ErrorState message="Unable to load analytics." />}
      
      <div className="grid gap-6 lg:grid-cols-2">
        {isLoadingAnalytics ? (
          <LoadingOverlay label="Loading analytics..." />
        ) : (
          <>
            <EngagementTrendline data={analytics?.engagement ?? []} />
            <EngagementDistributionChart 
              summary={sessionSummary} 
              latestSnapshot={snapshots[snapshots.length - 1] || null} 
            />
          </>
        )}
      </div>

      <EngagementHeatmap snapshots={currentSessionSnapshots} />
      
      {/* ✅ CORRECT PLACEMENT: Inside the main div, at the very end */}
      <Toaster position="top-center" richColors />
    </div>
  );
};